// src/components/User/UserMenu.tsx
import { useMediaQuery, Menu, MenuItem, Drawer, Box, Typography, Divider } from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material';
import { UserAvatar } from '../../shared/components/ui/Avatar/UserAvatar';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserStore, UserRole } from '../../app/store/useUserStore';
import { isSenderRole } from '../../shared/utils/roleUtils';
import { getUserTheme } from '../../shared/styles/theme/theme';
import { responsivePadding } from '../../shared/utils/responsive';
import { useRoleSwitch } from './hooks/useRoleSwitch';
import { performLogoutCleanup } from '../../shared/utils/logoutCleanup';

import settings from '@assets/settings.svg';
import LogoutIcon from '@assets/logout.svg';
import PlaneIcon from '@assets/Planegray.svg';
import WalletIcon from '@assets/wallet (1).svg';
import SwitchIcon from '@assets/zap.svg';
import PackageIcon from '@assets/Packagegray.svg';
import HomeIcon from '@assets/home.svg';
import AdminIcon from '@assets/admin.svg';

interface MenuOption {
  id: string;
  label: string;
  icon: string;
  path?: string;
  externalUrl?: string;
  onClick?: () => void;
  isDivider?: boolean;
  isAction?: boolean;
}

interface UserMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Rendered above the menu items on mobile only (e.g. the sender/traveler RoleToggle pill). */
  mobileHeaderSlot?: React.ReactNode;
}

/**
 * The single account/profile menu used by every navbar. Renders as a dropdown Menu next to the
 * avatar on tablet/desktop, and as a top Drawer with the same items on mobile — one place for
 * the menu's content instead of each navbar hand-rolling its own mobile drawer.
 */
const UserMenu: React.FC<UserMenuProps> = ({ anchorEl, open, onClose, mobileHeaderSlot }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('lg'));
  const { logout: auth0Logout } = useAuth0();
  const { user } = useUserStore();
  const { switchRole, isSwitching } = useRoleSwitch();
  const theme = getUserTheme(user?.user_role || UserRole.TRAVELLER);


  const handleRoleSwitch = async () => {
    const success = await switchRole();
    if (success) {
      onClose();
    }
    // If failed, keep menu open so user can see error and try again
  };

  const handleLogout = async () => {
    performLogoutCleanup();
    await auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
        federated: true,
      },
    });
    onClose();
  };

  const handleMenuItemClick = (option: MenuOption) => {
    if (option.onClick) option.onClick();
    else if (option.externalUrl) {
      onClose();
      window.open(option.externalUrl, '_blank', 'noopener,noreferrer');
    } else if (option.path) {
      onClose();

      // On the profile/account-security page, Auth0 state can become unstable
      // (e.g. after re-auth iframe logout), causing client-side navigation to
      // fail (URL changes but page doesn't render). Use a full page reload
      // to ensure clean navigation when leaving the profile page.
      if (location.pathname === '/update-profile' && option.path !== '/update-profile') {
        window.location.href = option.path;
        return;
      }

      // Using setTimeout to ensure menu closes before navigation starts
      setTimeout(() => {
        navigate(option.path!);
      }, 0);
    }
  };

  const isAdmin = user?.has_admin_portal_access ?? false;

  const getTravellerOptions = (): MenuOption[] => [
    { id: 'home', label: 'Home', icon: HomeIcon, path: '/' },
    { id: 'profile', label: 'Profile Settings', icon: settings, path: '/update-profile' },
    { id: 'travels', label: 'My Travels', icon: PlaneIcon, path: '/my-travels' },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/update-profile?tab=wallet' },
    { id: 'switch', label: 'Switch to Sender', icon: SwitchIcon, onClick: handleRoleSwitch, isAction: true },
    ...(isAdmin ? [{ id: 'admin', label: 'Go to Admin', icon: AdminIcon, externalUrl: import.meta.env.VITE_ADMIN_URL }] : []),
    { id: 'divider', label: '', icon: '', isDivider: true },
    { id: 'logout', label: 'Sign out', icon: LogoutIcon, onClick: handleLogout, isAction: true },
  ];

  const getSenderOptions = (): MenuOption[] => [
    { id: 'home', label: 'Home', icon: HomeIcon, path: '/' },
    { id: 'profile', label: 'Profile Settings', icon: settings, path: '/update-profile' },
    { id: 'orders', label: 'My Items', icon: PackageIcon, path: '/my-items' },
    { id: 'switch', label: 'Switch to Traveler', icon: SwitchIcon, onClick: handleRoleSwitch, isAction: true },
    ...(isAdmin ? [{ id: 'admin', label: 'Go to Admin', icon: AdminIcon, externalUrl: import.meta.env.VITE_ADMIN_URL }] : []),
    { id: 'divider', label: '', icon: '', isDivider: true },
    { id: 'logout', label: 'Sign out', icon: LogoutIcon, onClick: handleLogout, isAction: true },
  ];

  const menuOptions = isSenderRole(user?.user_role) ? getSenderOptions() : getTravellerOptions();

  const avatarHeader = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        borderBottom: '1px solid #E9EAEB',
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <UserAvatar
          src={user?.profile_image}
          name={user?.full_name || user?.name}
          email={user?.email}
          userRole={user?.user_role}
          size={40}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '10px',
            height: '10px',
            right: 0,
            bottom: 0,
            bgcolor: '#17B26A',
            border: '1.5px solid #FFFFFF',
            borderRadius: '50%',
          }}
        />
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '21px',
            color: '#414651',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {user?.full_name || user?.name || 'User'}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '18px',
            color: '#535862',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {user?.email}
        </Typography>
      </Box>
    </Box>
  );

  const menuItems = (variant: 'menu' | 'drawer') =>
    menuOptions.map(option => {
      if (option.isDivider) {
        return (
          <Box key={option.id} sx={{ padding: '4px 0' }}>
            <Divider sx={{ bgcolor: '#E9EAEB' }} />
          </Box>
        );
      }

      return (
        <MenuItem
          key={option.id}
          onClick={() => handleMenuItemClick(option)}
          component={variant === 'drawer' ? 'div' : 'li'}
          sx={{
            padding: '1px 6px',
            '&:hover': {
              bgcolor: 'transparent',
              '& .menu-content': { bgcolor: '#F9FAFB' },
            },
          }}
        >
          <Box
            className="menu-content"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              padding: '8px 10px',
              borderRadius: '6px',
              transition: 'background-color 0.2s',
            }}
          >
            <img
              src={option.icon}
              alt={option.label}
              style={{
                width: '16px',
                height: '16px',
                flexShrink: 0,
              }}
            />

            <Typography
              sx={{
                flex: 1,
                fontFamily: 'Poppins',
                fontWeight: option.isAction && option.id === 'logout' ? 600 : 400,
                fontSize: '14px',
                lineHeight: '21px',
                color: option.isAction && option.id === 'logout' ? '#B91C1C' : '#414651',
              }}
            >
              {option.label}
            </Typography>
          </Box>
        </MenuItem>
      );
    });

  if (isMobile) {
    return (
      <Drawer
        anchor="top"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            marginTop: '80px',
            maxHeight: 'calc(100dvh - 80px)',
            '@media (max-width: 310px) and (max-height: 300px)': {
              marginTop: '58px',
              maxHeight: 'calc(100dvh - 58px)',
            },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '0 0 16px 16px',
            boxShadow:
              '0px 12px 16px -4px rgba(10, 13, 18, 0.08), 0px 4px 6px -2px rgba(10, 13, 18, 0.03), 0px 2px 2px -1px rgba(10, 13, 18, 0.04)',
          },
        }}
      >
        <Box
          sx={{
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
            scrollbarWidth: 'thin',
            scrollbarColor: '#D9DADF transparent',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: '#D9DADF', borderRadius: '3px' },
          }}
        >
          {avatarHeader}
          {mobileHeaderSlot && (
            <Box sx={{ padding: '12px 16px 0' }}>{mobileHeaderSlot}</Box>
          )}
          <Box sx={{ padding: responsivePadding.container.mobile }}>
            {menuItems('drawer')}
          </Box>
        </Box>
      </Drawer>
    );
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        '& .MuiPaper-root': {
          width: '248px',
          mt: 1,
          borderRadius: '8px',
          border: '1px solid #E9EAEB',
          boxShadow:
            '0px 12px 16px -4px rgba(10, 13, 18, 0.08), 0px 4px 6px -2px rgba(10, 13, 18, 0.03), 0px 2px 2px -1px rgba(10, 13, 18, 0.04)',
          overflow: 'visible',
        },
        '& .MuiList-root': { padding: 0 },
      }}
    >
      {avatarHeader}
      <Box sx={{ padding: '4px 0' }}>
        {menuItems('menu')}
      </Box>
    </Menu>
  );
};

export default UserMenu;
